/**
 * SparkIndexedDBService.js
 * Manages Spark data storage using IndexedDB
 */

const DB_NAME = "SparksDB";
const DB_VERSION = 1;
const STORE_NAME = "sparks";

class SparkIndexedDBService {
    constructor() {
        this.db = null;
    }

    /**
     * Initialize/open the IndexedDB database
     * @returns {Promise<IDBDatabase>}
     */
    async openDB() {
        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error("IndexedDB failed to open:", request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log("IndexedDB opened successfully");
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create object store if it doesn't exist
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const objectStore = db.createObjectStore(STORE_NAME, {
                        keyPath: "spark_id"
                    });

                    // Create indexes for efficient querying
                    objectStore.createIndex("spark_name", "spark_name", { unique: false });
                    objectStore.createIndex("certified", "certified", { unique: false });
                    objectStore.createIndex("creation_date", "creation_date", { unique: false });
                    objectStore.createIndex("last_modified_date", "last_modified_date", { unique: false });
                    objectStore.createIndex("total_likes", "total_likes", { unique: false });
                    objectStore.createIndex("user_like", "user_like", { unique: false });

                    console.log("IndexedDB object store created with indexes");
                }
            };
        });
    }

    /**
     * Save a single spark to IndexedDB
     * @param {Object} spark - Complete spark object
     * @returns {Promise<number>} - Returns spark_id
     */
    async saveSpark(spark) {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.put(spark);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error("Failed to save spark:", request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Save multiple sparks in a single transaction (bulk insert/update)
     * @param {Array} sparks - Array of spark objects
     * @returns {Promise<number>} - Number of sparks saved
     */
    async saveSparks(sparks) {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const store = transaction.objectStore(STORE_NAME);
            let savedCount = 0;

            sparks.forEach(spark => {
                store.put(spark);
                savedCount++;
            });

            transaction.oncomplete = () => {
                console.log(`✅ Saved ${savedCount} sparks to IndexedDB`);
                resolve(savedCount);
            };

            transaction.onerror = () => {
                console.error("Transaction failed:", transaction.error);
                reject(transaction.error);
            };
        });
    }

    /**
     * Get a single spark by spark_id
     * @param {number} sparkId
     * @returns {Promise<Object|null>}
     */
    async getSpark(sparkId) {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readonly");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(sparkId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => {
                console.error("Failed to get spark:", request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get all sparks from IndexedDB
     * @returns {Promise<Array>}
     */
    async getAllSparks() {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readonly");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.getAll();

            request.onsuccess = () => {
                console.log(`✅ Retrieved ${request.result.length} sparks from IndexedDB`);
                resolve(request.result);
            };

            request.onerror = () => {
                console.error("Failed to get all sparks:", request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Delete a spark by spark_id
     * @param {number} sparkId
     * @returns {Promise<void>}
     */
    async deleteSpark(sparkId) {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(sparkId);

            request.onsuccess = () => {
                console.log(`✅ Deleted spark ${sparkId}`);
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clear all sparks (useful for force refresh)
     * @returns {Promise<void>}
     */
    async clearAllSparks() {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onsuccess = () => {
                console.log("✅ Cleared all sparks from IndexedDB");
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get the count of stored sparks
     * @returns {Promise<number>}
     */
    async getSparkCount() {
        const db = await this.openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readonly");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.count();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Search sparks by name (case-insensitive)
     * @param {string} searchQuery
     * @returns {Promise<Array>}
     */
    async searchSparksByName(searchQuery) {
        const allSparks = await this.getAllSparks();
        const query = searchQuery.toLowerCase();

        return allSparks.filter(spark =>
            spark.spark_name?.toLowerCase().includes(query) ||
            spark.spark_description?.toLowerCase().includes(query) ||
            spark.tags?.some(tag => tag.toLowerCase().includes(query))
        );
    }

    /**
     * Get cache metadata from localStorage
     * @returns {Object|null}
     */
    getCacheMetadata() {
        try {
            const meta = localStorage.getItem("spark_cache_metadata");
            return meta ? JSON.parse(meta) : null;
        } catch (e) {
            console.warn("Failed to read cache metadata:", e);
            return null;
        }
    }

    /**
     * Save cache metadata to localStorage
     * @param {Object} metadata
     */
    saveCacheMetadata(metadata) {
        try {
            localStorage.setItem("spark_cache_metadata", JSON.stringify(metadata));
        } catch (e) {
            console.warn("Failed to save cache metadata:", e);
        }
    }
}

// Export singleton instance
const sparkDBService = new SparkIndexedDBService();
export default sparkDBService;